import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, MessageCircle, Share2, ShoppingBag } from "lucide-react";
import { api, errMsg } from "../lib/api";

export default function PublicCatalog() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/catalog/${slug}`);
        setCat(data);
        document.title = `${data.name} · ${data.brand_name}`;
      } catch (e) {
        setErr(errMsg(e, "Catalog not found"));
      }
    })();
  }, [slug]);

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-[#0A0708] text-white text-sm">
      {err}
    </div>
  );
  if (!cat) return (
    <div className="min-h-screen grid place-items-center bg-[#0A0708] text-white/60 text-sm">
      Loading catalog…
    </div>
  );

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`Check out ${cat.brand_name}'s catalog: ${shareUrl}`)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0708] via-[#15080A] to-[#0A0708] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#4F46E5] font-bold">
            {cat.brand_name}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">
            {cat.name}
          </h1>
          {cat.description && (
            <p className="text-sm text-white/65 mt-3 max-w-xl mx-auto">
              {cat.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {(cat.products || []).map((p, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/[0.04] border border-white/10 overflow-hidden flex flex-col"
              data-testid={`public-product-${i}`}
            >
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt={p.name}
                  className="w-full aspect-[3/4] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full aspect-[3/4] grid place-items-center bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 text-white/30 text-xs">
                  no image
                </div>
              )}
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="font-semibold text-sm leading-tight line-clamp-2">
                  {p.name || "Untitled"}
                </div>
                {p.price && (
                  <div className="text-lg font-black text-[#4F46E5]">
                    ₹{p.price}
                  </div>
                )}
                {p.buy_url && (
                  <a
                    href={p.buy_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-auto inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-bold"
                    data-testid={`public-buy-${i}`}
                  >
                    <ShoppingBag size={12} /> Buy now
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-3">
          {cat.contact_phone && (
            <a
              href={`https://wa.me/${cat.contact_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi! I saw your catalog and want to order.")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] hover:bg-[#1FBA56] text-black text-sm font-black"
              data-testid="public-chat-seller"
            >
              <MessageCircle size={14} /> Chat with seller on WhatsApp
            </a>
          )}
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-xs"
          >
            <Share2 size={11} /> Share this catalog
          </a>
        </div>

        <div className="text-center text-[10px] text-white/30 mt-12">
          Made with <span className="text-[#4F46E5] font-bold">EcomCatalog AI</span> · ecomcatalog.ai
        </div>
      </div>
    </div>
  );
}
